/* eslint-disable max-classes-per-file */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import { partition } from 'lodash';
import {
  compressToEncodedURIComponent,
  decompressFromEncodedURIComponent,
} from 'lz-string';
import { createSearchParams, URLSearchParamsInit } from 'react-router-dom';

import {
  DataType,
  isDataType,
  FilterState,
  isFilterableField,
  isOrderableField,
  OrderableField,
  ArchitectFilterState,
  isArchitectFilterableField,
} from '../types';

// Keys for the URL search params
const DATA_TYPE_URL_FIELD = 'data_type';
const ORDER_BY_URL_FIELD = 'order_by';
const FRAME_INDEX_FIELD = 'frame';
const SCENE_STATE_URL_FIELD = 'scene';
const SCENE_STATE_VERSION = 1;
const MIN_POINT_SIZE = 1;
const MAX_POINT_SIZE = 50;

export type CameraShareState = {
  cameraType: 'orthographic' | 'perspective';
  position: [number, number, number];
  target: [number, number, number];
  zoom: number;
};

export type SceneShareState = {
  v: typeof SCENE_STATE_VERSION;
  camera?: CameraShareState;
  pointSize?: number;
  showRangeRings?: boolean;
  is2DImageVisible?: boolean;
  leftSidebarExpanded?: boolean;
  selectedImagePanelSn?: string;
  selectedSensorImageSettings?: Array<{
    showImage?: boolean;
    coloringMode?: string;
    colorPalette?: string;
    isAutoLevelsOn?: boolean;
    isNoiseSuppressionOn?: boolean;
  }>;
};

type SharedImageSetting = NonNullable<
  SceneShareState['selectedSensorImageSettings']
>[number];

const isSharedImageSetting = (
  value: SharedImageSetting | undefined
): value is SharedImageSetting => value !== undefined;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isVec3 = (value: unknown): value is [number, number, number] =>
  Array.isArray(value) &&
  value.length === 3 &&
  value.every((entry) => isFiniteNumber(entry));

const clampZoom = (zoom: number): number => {
  const minZoom = 0.0001;
  return Math.max(zoom, minZoom);
};

const clampPointSize = (pointSize: number): number =>
  Math.min(Math.max(pointSize, MIN_POINT_SIZE), MAX_POINT_SIZE);

const normalizeSharedImageSetting = (
  value: unknown
): SharedImageSetting | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const rawSetting = value as {
    showImage?: unknown;
    coloringMode?: unknown;
    colorPalette?: unknown;
    isAutoLevelsOn?: unknown;
    isNoiseSuppressionOn?: unknown;
  };

  const nextSetting: SharedImageSetting = {};
  if (typeof rawSetting.showImage === 'boolean') {
    nextSetting.showImage = rawSetting.showImage;
  }
  if (typeof rawSetting.coloringMode === 'string') {
    nextSetting.coloringMode = rawSetting.coloringMode;
  }
  if (typeof rawSetting.colorPalette === 'string') {
    nextSetting.colorPalette = rawSetting.colorPalette;
  }
  if (typeof rawSetting.isAutoLevelsOn === 'boolean') {
    nextSetting.isAutoLevelsOn = rawSetting.isAutoLevelsOn;
  }
  if (typeof rawSetting.isNoiseSuppressionOn === 'boolean') {
    nextSetting.isNoiseSuppressionOn = rawSetting.isNoiseSuppressionOn;
  }

  return Object.keys(nextSetting).length > 0 ? nextSetting : undefined;
};

const normalizeCameraShareState = (
  value: unknown
): CameraShareState | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const camera = value as {
    cameraType?: string;
    position?: unknown;
    target?: unknown;
    zoom?: unknown;
  };

  if (
    (camera.cameraType !== 'orthographic' &&
      camera.cameraType !== 'perspective') ||
    !isVec3(camera.position) ||
    !isVec3(camera.target) ||
    !isFiniteNumber(camera.zoom)
  ) {
    return undefined;
  }

  return {
    cameraType: camera.cameraType,
    position: camera.position,
    target: camera.target,
    zoom: clampZoom(camera.zoom),
  };
};

const replaceSearchParamsSilently = (newSearchParams: URLSearchParams) => {
  const queryString = newSearchParams.toString();
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;
  window.history.replaceState(null, '', newUrl);
};

export function encodeSceneState(sceneState: SceneShareState): string {
  return compressToEncodedURIComponent(JSON.stringify(sceneState));
}

export function decodeSceneState(
  encodedSceneState: string | null
): SceneShareState | null {
  if (!encodedSceneState) {
    return null;
  }

  try {
    const decompressed = decompressFromEncodedURIComponent(encodedSceneState);
    if (!decompressed) {
      return null;
    }

    const parsed = JSON.parse(decompressed) as {
      v?: number;
      camera?: unknown;
      pointSize?: unknown;
      showRangeRings?: unknown;
      is2DImageVisible?: unknown;
      leftSidebarExpanded?: unknown;
      selectedImagePanelSn?: unknown;
      selectedSensorImageSettings?: unknown;
    };

    if (parsed.v !== SCENE_STATE_VERSION) {
      return null;
    }

    const normalizedCamera = normalizeCameraShareState(parsed.camera);
    const normalizedPointSize = isFiniteNumber(parsed.pointSize)
      ? clampPointSize(parsed.pointSize)
      : undefined;
    const normalizedRangeRings =
      typeof parsed.showRangeRings === 'boolean'
        ? parsed.showRangeRings
        : undefined;
    const normalized2DImageVisible =
      typeof parsed.is2DImageVisible === 'boolean'
        ? parsed.is2DImageVisible
        : undefined;
    const normalizedLeftSidebarExpanded =
      typeof parsed.leftSidebarExpanded === 'boolean'
        ? parsed.leftSidebarExpanded
        : undefined;
    const normalizedSelectedImagePanelSn =
      typeof parsed.selectedImagePanelSn === 'string'
        ? parsed.selectedImagePanelSn
        : undefined;
    const normalizedSelectedSensorImageSettings = Array.isArray(
      parsed.selectedSensorImageSettings
    )
      ? parsed.selectedSensorImageSettings
          .map((setting) => normalizeSharedImageSetting(setting))
          .filter(isSharedImageSetting)
      : undefined;

    return {
      v: SCENE_STATE_VERSION,
      ...(normalizedCamera ? { camera: normalizedCamera } : {}),
      ...(normalizedPointSize !== undefined
        ? { pointSize: normalizedPointSize }
        : {}),
      ...(normalizedRangeRings !== undefined
        ? { showRangeRings: normalizedRangeRings }
        : {}),
      ...(normalized2DImageVisible !== undefined
        ? { is2DImageVisible: normalized2DImageVisible }
        : {}),
      ...(normalizedLeftSidebarExpanded !== undefined
        ? { leftSidebarExpanded: normalizedLeftSidebarExpanded }
        : {}),
      ...(normalizedSelectedImagePanelSn !== undefined
        ? { selectedImagePanelSn: normalizedSelectedImagePanelSn }
        : {}),
      ...(normalizedSelectedSensorImageSettings &&
      normalizedSelectedSensorImageSettings.length > 0
        ? { selectedSensorImageSettings: normalizedSelectedSensorImageSettings }
        : {}),
    };
  } catch {
    return null;
  }
}

export function sceneStateFromSearchParams(
  searchParams: URLSearchParams
): SceneShareState | null {
  return decodeSceneState(searchParams.get(SCENE_STATE_URL_FIELD));
}

export function updateSceneInUrl(
  scenePatch: Partial<Omit<SceneShareState, 'v'>>,
  previousSearchParams = new URLSearchParams(window.location.search)
): void {
  const previousSceneState = sceneStateFromSearchParams(
    previousSearchParams
  ) ?? {
    v: SCENE_STATE_VERSION,
  };

  const mergedSceneState: SceneShareState = {
    ...previousSceneState,
    ...scenePatch,
    v: SCENE_STATE_VERSION,
  };

  const newSearchParams = new URLSearchParams(previousSearchParams);
  const hasScenePayload =
    Boolean(mergedSceneState.camera) ||
    mergedSceneState.pointSize !== undefined ||
    mergedSceneState.showRangeRings !== undefined ||
    mergedSceneState.is2DImageVisible !== undefined ||
    mergedSceneState.leftSidebarExpanded !== undefined ||
    mergedSceneState.selectedImagePanelSn !== undefined ||
    (Array.isArray(mergedSceneState.selectedSensorImageSettings) &&
      mergedSceneState.selectedSensorImageSettings.length > 0);

  if (!hasScenePayload) {
    newSearchParams.delete(SCENE_STATE_URL_FIELD);
  } else {
    newSearchParams.set(
      SCENE_STATE_URL_FIELD,
      encodeSceneState(mergedSceneState)
    );
  }

  replaceSearchParamsSilently(newSearchParams);
}

// This is the type for the searchParams setter provided by the
// useSearchParams hook of react-router-dom. It is not explicitely
// defined in the react-router-dom types so we define it here
type URLSearchParamsSetter = (
  nextInit: URLSearchParamsInit,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navigateOptions?: { replace?: boolean | undefined; state?: any } | undefined
) => void;

// Throughout the app, we want  all utility functions of this file to be
// able update the URL search params. Because we use react-router, we
// should not manipulate the history object directly
// (https://reactrouter.com/docs/en/v6/upgrading/v5). So we keep here a
// "URL search params setter" as a module-scope variable (defined only
// once for the whole app). By calling the 'registerSearchParamsSetter'
// function below, the App component can assign it to the setter
// provided by the useSearchParams hook of react-router.
let setSearchParams: URLSearchParamsSetter;
export const registerSearchParamsSetter = (
  searchParamsSetterToRegister: URLSearchParamsSetter
) => {
  if (setSearchParams !== searchParamsSetterToRegister) {
    setSearchParams = searchParamsSetterToRegister;
  }
};

/**
 * Partition object based on whether the key is a filter
 * @param filterObj Filter object
 * @returns tuple of objects ({[field: string]: string[]}) where the
 * first one's keys are filter fields, while the second one's keys are
 * not
 */
function partitionFilters(filterObj: { [field: string]: string[] }) {
  const [filters, nonFilters] = partition(
    Object.entries(filterObj),
    ([field]) => isFilterableField(field)
  );
  return [Object.fromEntries(filters), Object.fromEntries(nonFilters)];
}

function partitionArchitectSceneFilters(filterObj: {
  [field: string]: string[];
}) {
  const [filters, nonFilters] = partition(
    Object.entries(filterObj),
    ([field]) => isArchitectFilterableField(field)
  );
  return [Object.fromEntries(filters), Object.fromEntries(nonFilters)];
}

/**
 * Convert searchParams to an object whose values are all string[] for
 * easier processing
 *
 * It is helpful to use this to deal with multiple values that are the
 * same
 * @param searchParams URLSearchParams
 * @returns Search Params object where values are string[] only
 */
const searchParamsToObj: (searchParams: URLSearchParams) => {
  [k: string]: string[];
} = (searchParams: URLSearchParams) =>
  [...new Set(searchParams.keys())].reduce(
    (accumulatedObject, key) => ({
      ...accumulatedObject,
      ...{ [key]: searchParams.getAll(key) },
    }),
    {}
  );

/**
 * 'lean' representation of the filters that we can write/read to the
 * browser history query parameters. The filer fields and values are
 * accessed with the 'filters' property of instances of this class
 */
export class UrlFilterState {
  filters: { [filterField: string]: string[] };

  /**
   *
   */
  constructor(filtersObj: { [filterField: string]: string[] }) {
    const [filterFields, nonFilterFields] = partitionFilters(filtersObj);

    if (Object.keys(nonFilterFields).length > 0) {
      throw new Error(
        `Field(s) [${Object.keys(nonFilterFields).join(',')}] were added ` +
          `to the URL but they aren't filters. Will ignore them when ` +
          `creating a UrlFilterState object.`
      );
    }

    this.filters = filterFields;
  }

  /**
   * Get new url filter state from a filter state object
   * @param filterState input filter state
   * @returns new url filter state object
   */
  static fromFilterState(filterState: FilterState) {
    const filterStateObj: { [filterField: string]: string[] } = {};
    filterState.forEach((singleState) => {
      filterStateObj[singleState.sectionInfo.filterField] =
        singleState.values.map((dropdownData) => dropdownData.name);
    });

    return new UrlFilterState(filterStateObj);
  }

  /**
   * Architect filter state
   * @param filterState
   */
  static fromArchitectFilterState(filterState: ArchitectFilterState) {
    const filterStateObj: { [filterField: string]: string[] } = {};
    filterState.forEach((singleState) => {
      filterStateObj[singleState.sectionInfo.filterField] =
        singleState.values.map((dropdownData) => dropdownData.name);
    });

    return new UrlFilterState(filterStateObj);
  }

  /**
   *
   * @param searchParams
   */
  static fromSearchParams(searchParams: URLSearchParams) {
    const [filtersFields] = partitionFilters(searchParamsToObj(searchParams));
    return new UrlFilterState(filtersFields);
  }

  toSearchParams() {
    return createSearchParams(this.filters);
  }

  // Keep old filter fields but update fields present in filtersToUpdate
  update(filtersToUpdate: UrlFilterState) {
    return new UrlFilterState({
      ...this.filters,
      ...filtersToUpdate.filters,
    });
  }

  empty() {
    return Object.keys(this.filters).length === 0;
  }
}

export class UrlArchitectSceneFilterState {
  filters: { [filterField: string]: string[] };

  constructor(filtersObj: { [filterField: string]: string[] }) {
    const [filterFields, nonFilterFields] =
      partitionArchitectSceneFilters(filtersObj);

    if (Object.keys(nonFilterFields).length > 0) {
      throw new Error(
        `Field(s) [${Object.keys(nonFilterFields).join(',')}] were added ` +
          `to the URL but they aren't filters. Will ignore them when ` +
          `creating a UrlFilterState object.`
      );
    }

    this.filters = filterFields;
  }

  static fromFilterState(filterState: ArchitectFilterState) {
    const filterStateObj: { [filterField: string]: string[] } = {};
    filterState.forEach((singleState) => {
      filterStateObj[singleState.sectionInfo.filterField] =
        singleState.values.map((dropdownData) => dropdownData.name);
    });

    return new UrlArchitectSceneFilterState(filterStateObj);
  }

  static fromSearchParams(searchParams: URLSearchParams) {
    const [filtersFields] = partitionArchitectSceneFilters(
      searchParamsToObj(searchParams)
    );
    return new UrlArchitectSceneFilterState(filtersFields);
  }

  toSearchParams() {
    return createSearchParams(this.filters);
  }

  // Keep old filter fields but update fields present in filtersToUpdate

  update(filtersToUpdate: UrlArchitectSceneFilterState) {
    return new UrlArchitectSceneFilterState({
      ...this.filters,
      ...filtersToUpdate.filters,
    });
  }

  empty() {
    return Object.keys(this.filters).length === 0;
  }
}

/**
 * Update the 'frame' param of the URL query params
 *
 * Keep the rest of URL query params unchanged.
 * @param currentFrameIdx Frame index to add to URL
 * @param previousSearchParams current URL search params, needed so we
 * can preserve the params other than 'frame'. Defaults to grabbing it
 * from the current window.location (NOTE: only works with
 * BrowserRouter and not HashRouter)
 */
export function updateFrameInUrl(
  currentFrameIdx: number,
  previousSearchParams = new URLSearchParams(window.location.search)
): void {
  const newSearchParams = new URLSearchParams(previousSearchParams);
  newSearchParams.set(FRAME_INDEX_FIELD, currentFrameIdx.toString());
  replaceSearchParamsSilently(newSearchParams);
}

/**
 * Set filters in URL query params
 *
 * Keep URL query params which are not filterable fields unchanged (like
 * 'order_by'), but remove any previous filter values which were in the
 * query params
 * @param newFilterState filters state stored as a UrlFilterState
 * instance
 * @param previousSearchParams current URL search params, needed so we
 * can preserve the params other than filters. Defaults to grabbing it
 * from the current window.location (NOTE: only works with BrowserRouter
 * and not HashRouter)
 */
export function setFiltersInUrl(
  newFilterState: UrlFilterState,
  previousSearchParams = new URLSearchParams(window.location.search)
): void {
  // Keep around the query fields which aren't specific to filters
  const [, nonFilterFields] = partitionFilters(
    searchParamsToObj(previousSearchParams)
  );

  const newSearchParams = createSearchParams({
    ...nonFilterFields,
    ...newFilterState.filters,
  });
  setSearchParams(newSearchParams, { replace: true });
}

/**
 * Set filters in URL query params
 *
 * Keep URL query params which are not filterable fields unchanged (like
 * 'order_by'), but remove any previous filter values which were in the
 * query params
 * @param newFilterState filters state stored as a UrlFilterState
 * instance
 * @param previousSearchParams current URL search params, needed so we
 * can preserve the params other than filters. Defaults to grabbing it
 * from the current window.location (NOTE: only works with BrowserRouter
 * and not HashRouter)
 */
export function setArchitectSceneFiltersInUrl(
  newFilterState: UrlFilterState,
  previousSearchParams = new URLSearchParams(window.location.search)
): void {
  // Keep around the query fields which aren't specific to filters
  const [, nonFilterFields] = partitionArchitectSceneFilters(
    searchParamsToObj(previousSearchParams)
  );

  const newSearchParams = createSearchParams({
    ...nonFilterFields,
    ...newFilterState.filters,
  });
  setSearchParams(newSearchParams, { replace: true });
}

/**
 * Update filters in URL query params
 *
 * Keep all other URL query params, include previous filterable fields,
 * unchanged.
 * @param filtersToUpdate filters state stored as a UrlFilterState
 * instance
 * @param previousSearchParams current URL search params, needed so we
 * can preserve the params other than filters. Defaults to grabbing it
 * from the current window.location (NOTE: only works with BrowserRouter
 * and not HashRouter)
 */
export function updateFiltersInUrl(
  filtersToUpdate: FilterState,
  previousSearchParams = new URLSearchParams(window.location.search)
): void {
  const urlFiltersToUpdate = UrlFilterState.fromFilterState(filtersToUpdate);
  const previousUrlFilters =
    UrlFilterState.fromSearchParams(previousSearchParams);

  const newFilters = previousUrlFilters.update(urlFiltersToUpdate);
  setFiltersInUrl(newFilters, previousSearchParams);
}

/**
 * Update filters in URL query params
 *
 * Keep all other URL query params, include previous filterable fields,
 * unchanged.
 * @param filtersToUpdate filters state stored as a UrlFilterState
 * instance
 * @param previousSearchParams current URL search params, needed so we
 * can preserve the params other than filters. Defaults to grabbing it
 * from the current window.location (NOTE: only works with BrowserRouter
 * and not HashRouter)
 */
export function updateArchitectFiltersInUrl(
  filtersToUpdate: ArchitectFilterState,
  previousSearchParams = new URLSearchParams(window.location.search)
): void {
  const urlFiltersToUpdate =
    UrlArchitectSceneFilterState.fromFilterState(filtersToUpdate);
  const previousUrlFilters =
    UrlArchitectSceneFilterState.fromSearchParams(previousSearchParams);

  const newFilters = previousUrlFilters.update(urlFiltersToUpdate);
  setArchitectSceneFiltersInUrl(newFilters, previousSearchParams);
}

/**
 * Set order_by URL query param
 *
 * Keep the rest of URL query params unchanged.
 * @param orderField OrderableField to add to URL
 * @param previousSearchParams current URL search params, needed so we
 * can preserve the params other than 'order_by'. Defaults to grabbing
 * it from the current window.location (NOTE: only works with
 * BrowserRouter and not HashRouter)
 */
export function setOrderFieldInUrl(
  orderField: OrderableField,
  previousSearchParams = new URLSearchParams(window.location.search)
) {
  const newSearchParams = new URLSearchParams(previousSearchParams);
  newSearchParams.set(ORDER_BY_URL_FIELD, orderField);
  setSearchParams(newSearchParams, { replace: true });
}

/**
 * Add data_type to URLsearch params
 *
 * If the data type to write to the URL is SDK, then don't add it to the
 * URL. An empty data_type query param is treated as SDK data so as to
 * not bloat the URL
 *
 * Keep the rest of URL query params unchanged.
 * @param dataType DataType to add to URL
 * @param previousSearchParams current URL search params, needed so we
 * can preserve the params other than 'data_type'. Defaults to grabbing
 * it from the current window.location (NOTE: only works with
 * BrowserRouter and not HashRouter)
 */
export function setDataTypeInUrl(
  dataType: DataType,
  previousSearchParams = new URLSearchParams(window.location.search)
) {
  const newSearchParams = new URLSearchParams(previousSearchParams);

  // Treat empty data_type query param in URL as SDK data. This is to
  // not bloat the URL
  if (dataType === DataType.Sdk) {
    newSearchParams.delete(DATA_TYPE_URL_FIELD);
  } else {
    newSearchParams.set(DATA_TYPE_URL_FIELD, dataType);
  }

  setSearchParams(newSearchParams, { replace: true });
}

/**
 * Extract order field from URL Search Params
 * @param searchParams object
 * @returns OrderableField specified in the query params, or null if
 * none was found
 */
export function orderFieldFromSearchParams(searchParams: URLSearchParams) {
  const orderFields = searchParams.getAll(ORDER_BY_URL_FIELD);

  if (orderFields.length > 1) {
    console.warn(
      `Multiple fields were provided for the list ordering ` +
        `[${orderFields}]. Will only keep the first.`
    );
  }

  const firstOrderField = orderFields.length === 0 ? null : orderFields[0];

  if (firstOrderField && !isOrderableField(firstOrderField)) {
    console.warn(
      `Cannot order list with field "${firstOrderField}". Will use default instead`
    );
    return null;
  }

  return firstOrderField ? (firstOrderField as OrderableField) : null;
}

export function orderArchitectFieldFromSearchParams(
  searchParams: URLSearchParams
) {
  const orderFields = searchParams.getAll(ORDER_BY_URL_FIELD);

  if (orderFields.length > 1) {
    console.warn(
      `Multiple fields were provided for the list ordering ` +
        `[${orderFields}]. Will only keep the first.`
    );
  }

  const firstOrderField = orderFields.length === 0 ? null : orderFields[0];

  return firstOrderField ? (firstOrderField as OrderableField) : null;
}

/**
 * Extract data type from URL Search Params
 * @param searchParams object
 * @returns DataType specified in the query params, or null if
 * none was found
 */
export function dataTypeFromSearchParams(searchParams: URLSearchParams) {
  const dataTypeFields = searchParams.getAll(DATA_TYPE_URL_FIELD);

  if (dataTypeFields.length > 1) {
    console.warn(
      `Multiple fields were provided for data type ` +
        `[${dataTypeFields}]. Will only keep the first.`
    );
  }
  const firstDataTypeField =
    dataTypeFields.length === 0 ? null : dataTypeFields[0];

  if (firstDataTypeField && !isDataType(firstDataTypeField)) {
    console.warn(
      `"${firstDataTypeField}" is not a data type. Will use default instead`
    );
    return null;
  }

  return firstDataTypeField ? (firstDataTypeField as DataType) : null;
}
